import {
  PUBLIC_BRAND_LOGO_SRC,
  PUBLIC_BRAND_SITE_URL,
  PUBLIC_BRAND_WORDMARK,
} from '@core/branding/public-brand';
import {
  MarketingWebsiteFaqEntry,
  MarketingWebsitePageId,
  MarketingWebsiteSeoConfig,
} from './marketing-website-seo.models';
import { truncateMetaDescription } from './public-page-seo.utils';

const MARKETING_SITE_ORIGIN = PUBLIC_BRAND_SITE_URL;

/** Rutas canónicas bajo milinco.com (sin trailing slash). */
export function buildMarketingCanonicalUrl(origin: string, path: string): string {
  const base = origin.replace(/\/$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  if (normalizedPath === '/') {
    return base;
  }
  return `${base}${normalizedPath}`;
}

export function resolveMarketingSiteOrigin(origin?: string): string {
  const trimmed = origin?.trim();
  if (trimmed) {
    return trimmed.replace(/\/$/, '');
  }
  return MARKETING_SITE_ORIGIN;
}

export function buildMarketingDocumentTitle(pageTitle: string): string {
  const page = pageTitle.trim();
  return page ? `${page} | ${PUBLIC_BRAND_WORDMARK}` : PUBLIC_BRAND_WORDMARK;
}

export function buildMarketingShareImageUrl(origin: string): string {
  return `${origin.replace(/\/$/, '')}${PUBLIC_BRAND_LOGO_SRC}`;
}

const SOFTWARE_FEATURE_LIST = [
  'Página pública del negocio con enlaces y marca',
  'Catálogo online con variantes, modificadores y promociones',
  'Pedidos y checkout con domicilio o recoger',
  'Servicios con solicitudes estructuradas',
  'Punto de venta con mesas, pedidos y tablero de preparación',
  'Clientes, inventario, finanzas y compras',
  'Multi-usuario con roles y permisos',
  'Multi-sede para negocios en crecimiento',
] as const;

export function buildMarketingOrganizationJsonLd(origin: string): Record<string, unknown> {
  return {
    '@type': 'Organization',
    name: PUBLIC_BRAND_WORDMARK,
    url: origin,
    logo: buildMarketingShareImageUrl(origin),
    description: truncateMetaDescription(
      'Plataforma colombiana multi-rubro para dueños de negocio: página, tienda, ventas, POS e inventario en un solo lugar.'
    ),
    areaServed: {
      '@type': 'Country',
      name: 'Colombia',
    },
  };
}

export function buildMarketingWebSiteJsonLd(origin: string): Record<string, unknown> {
  return {
    '@type': 'WebSite',
    name: PUBLIC_BRAND_WORDMARK,
    url: origin,
    inLanguage: 'es-CO',
    publisher: {
      '@type': 'Organization',
      name: PUBLIC_BRAND_WORDMARK,
    },
  };
}

export function buildMarketingSoftwareApplicationJsonLd(
  origin: string,
  description: string
): Record<string, unknown> {
  return {
    '@type': 'SoftwareApplication',
    name: PUBLIC_BRAND_WORDMARK,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    url: origin,
    description: truncateMetaDescription(description),
    featureList: [...SOFTWARE_FEATURE_LIST],
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'COP',
      description: 'Prueba 30 días plan Negocio',
    },
    areaServed: {
      '@type': 'Country',
      name: 'Colombia',
    },
  };
}

export function buildMarketingFaqPageJsonLd(
  entries: readonly MarketingWebsiteFaqEntry[]
): Record<string, unknown> | null {
  if (entries.length === 0) {
    return null;
  }
  return {
    '@type': 'FAQPage',
    mainEntity: entries.map((entry) => ({
      '@type': 'Question',
      name: entry.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: entry.answer,
      },
    })),
  };
}

export function buildMarketingPageJsonLd(config: MarketingWebsiteSeoConfig): string {
  const origin = resolveMarketingSiteOrigin(config.origin);
  const canonicalUrl = buildMarketingCanonicalUrl(origin, config.path);
  const graph: Record<string, unknown>[] = [
    buildMarketingOrganizationJsonLd(origin),
    buildMarketingWebSiteJsonLd(origin),
    buildMarketingSoftwareApplicationJsonLd(origin, config.metaDescription),
    {
      '@type': 'WebPage',
      name: config.documentTitle,
      url: canonicalUrl,
      description: truncateMetaDescription(config.metaDescription),
      isPartOf: {
        '@type': 'WebSite',
        url: origin,
      },
    },
  ];

  const faq = buildMarketingFaqPageJsonLd(config.faqEntries ?? []);
  if (faq) {
    graph.push(faq);
  }

  return JSON.stringify({
    '@context': 'https://schema.org',
    '@graph': graph,
  });
}

/** Paths de marketing indexables (milinco.com). */
export function marketingPathForPageId(pageId: MarketingWebsitePageId): string {
  switch (pageId) {
    case 'home':
      return '/';
    case 'pricing':
      return '/precios';
    case 'plan':
      return '/plan';
    case 'about':
      return '/que-es-milinco';
    case 'vertical-solo-link':
      return '/para/solo-link';
    case 'vertical-tiendas':
      return '/para/tiendas';
    case 'vertical-servicios':
      return '/para/servicios';
    case 'vertical-restaurantes':
      return '/para/restaurantes';
    case 'vertical-pos':
      return '/para/pos';
    case 'vertical-farmacias':
      return '/para/farmacias';
    case 'vertical-ferreterias':
      return '/para/ferreterias';
    case 'vertical-barberias':
      return '/para/barberias';
    case 'vertical-produccion':
      return '/para/produccion';
  }
}

export function isMarketingWebsitePageId(value: string): value is MarketingWebsitePageId {
  return (
    value === 'home' ||
    value === 'pricing' ||
    value === 'plan' ||
    value === 'about' ||
    value === 'vertical-solo-link' ||
    value === 'vertical-tiendas' ||
    value === 'vertical-servicios' ||
    value === 'vertical-restaurantes' ||
    value === 'vertical-pos' ||
    value === 'vertical-farmacias' ||
    value === 'vertical-ferreterias' ||
    value === 'vertical-barberias' ||
    value === 'vertical-produccion'
  );
}
